import EntityFilter from "src/components/common/EntityFilter";
import { Box, CircularProgress } from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import { useState } from "react";
import { useQuery } from "react-query";
import { createSearchParams, Link as RouterLink } from "react-router-dom";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import DataGrid from "src/components/common/DataGrid";
import EmptyView from "src/components/common/EmptyView";
import { Button } from "src/components/common/FormComponents/Buttons";
import { getTreePlantation } from "src/utils/api/createProjectFlow";
import {
  dateAndTimeStamp,
  downloadSheet,
  formatToIndianCurrency,
} from "src/utils/helper";
import { entityStatusMessages } from "src/constants/common";
import defaultStyles, { StylesClasses } from "./styles";
import { path } from "src/constants/path";
import SearchBar from "src/components/common/SearchField";
import { getStyles } from "src/styles/theme";
import { useAppSelector } from "src/redux/store";

const Monitoring = () => {
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);

  const styles = getStyles<StylesClasses>(defaultStyles);

  const user = useAppSelector((state) => state.root.user);

  const [queryParams, setQueryParams] = useState<{
    offset: number;
    limit: number;
    download: boolean;
    sortBy: string;
    searchText: string;
    status: string;
    entityIds?: string;
  }>({
    offset: 0,
    limit: 50,
    download: false,
    sortBy: "updated_at:DESC",
    searchText: "",
    status: "APPROVED",
  });

  const { isLoading, isFetching } = useQuery(
    ["monitoringApprovedProjects", queryParams],
    () =>
      getTreePlantation({
        ...queryParams,
      }),
    {
      onSuccess: (res: AxiosResponse) => {
        const rows = res.data.rows.map((row: any) => ({
          sno: row?.serialNo,
          id: row.id,
          registrationNo: row.registrationNo,
          entityName: row.user?.firstName,
          state: row.state ?? "-",
          district: row?.district ?? "-",
          division: row?.projectDetail?.landSpecification?.division,
          totalArea: `${row.totalArea}`,
          finalArea: row?.netPlantationArea ?? "-",
          ecoRestorationCost: formatToIndianCurrency(row?.demandNote) ?? "-",
          adminCharges: formatToIndianCurrency(row?.icfreCharges) ?? "-",
          demandNote: row.demandNote
            ? formatToIndianCurrency(
                +`${Number(row?.demandNote)}` + +`${Number(row?.icfreCharges)}`
              )
            : "-",
          totalAmountPaid: formatToIndianCurrency(row.amountPaid) ?? "-",
          ecoRestorationCostPaid:
            formatToIndianCurrency(row.ecoRestorationCost) ?? "-",
          adminChargesPaid: formatToIndianCurrency(row.adminCharges) ?? "-",
          status: entityStatusMessages[row.status] ?? row.status,
          created_at: dateAndTimeStamp(row?.created_at, "IST"),
          updated_at: dateAndTimeStamp(row?.updated_at, "IST"),
        }));

        if (queryParams.download) {
          const exportData = rows.map((item: any) => ({
            "S.No": item?.sno,
            EPID: item?.id,
            "Registration No": item?.registrationNo,
            "Entity Name": item?.entityName,
            State: item?.state,
            District: item?.district,
            Division: item?.division,
            "Selected Area (Ha)": item?.totalArea,
            "Final Area (Ha)": item?.finalArea,
            "Eco-Restoration Cost (₹)": item?.ecoRestorationCost,
            "Admin Charges (₹)": item?.adminCharges,
            "Demand Note Amount (₹)": item?.demandNote,
            "Total Amount Paid (₹)": item?.totalAmountPaid,
            "Eco-Restoration Cost Paid (₹)": item?.ecoRestorationCostPaid,
            "Admin Charges Paid (₹)": item?.adminChargesPaid,
            Status: item?.status,
            "Created At": item?.created_at,
            "Updated At": item?.updated_at,
          }));
          downloadSheet(exportData);
          setQueryParams((prev) => ({
            ...prev,
            download: false,
          }));
        } else {
          setTableRows(rows);
          setRowCount(res.data.count);
        }
      },
      onError: (err: AxiosError) => {},
      refetchOnMount: true,
    }
  );

  const RenderEPID = (params: any) => {
    return (
      <Box
        component={RouterLink}
        to={`${path.landOwnerShipDetailView}?${createSearchParams({
          id: params?.row?.id,
        })}`}
        sx={{ ...defaultStyles.renderButtonText2 }}
      >{`${params?.row?.id}`}</Box>
    );
  };

  const RenderUploadReport = (params: any) => {
    return (
      <Box
        component={RouterLink}
        to={`${path.monitoringReport}?${createSearchParams({
          epid: params?.row?.id,
        })}`}
        sx={{ ...defaultStyles.renderButtonText2 }}
      >
        Upload Report
      </Box>
    );
  };

  const RenderOldMonitoring = (params: any) => {
    return (
      <Box
        component={RouterLink}
        to={`${path.addMonitoringDetails}?${createSearchParams({
          id: params?.row?.id,
        })}`}
        sx={{ ...defaultStyles.renderButtonText2 }}
      >
        View Details
      </Box>
    );
  };

  const tableColumns: any = [
    {
      field: "sno",
      headerName: "S.No",
      width: 90,
      sortable: false,
    },
    {
      field: "id",
      headerName: "EPID",
      width: 250,
      sortable: false,
      renderCell: RenderEPID,
    },
    {
      field: "oldMonitoring",
      headerName: "Monitoring Details",
      width: 170,
      sortable: false,
      renderCell: RenderOldMonitoring,
    },
    {
      field: "uploadReport",
      headerName: "Upload Report",
      width: 170,
      sortable: false,
      renderCell: RenderUploadReport,
    },

    {
      field: "registrationNo",
      headerName: "Reg.No",
      width: 100,
      sortable: false,
    },
    {
      field: "entityName",
      headerName: "Entity Name",
      width: 150,
      sortable: true,
    },
    {
      field: "state",
      headerName: "State",
      width: 150,
      sortable: true,
    },
    {
      field: "district",
      headerName: "District",
      width: 150,
      sortable: true,
    },
    {
      field: "division",
      headerName: "Division",
      width: 150,
      sortable: true,
    },
    {
      field: "totalArea",
      headerName: "Selected Area(Ha)",
      width: 170,
      sortable: false,
    },
    {
      field: "finalArea",
      headerName: "Final Area(Ha)",
      width: 150,
      sortable: false,
    },
    {
      field: "ecoRestorationCost",
      headerName: "Eco-Restoration Cost",
      width: 200,
      sortable: false,
    },
    {
      field: "adminCharges",
      headerName: "Admin Charges",
      width: 170,
      sortable: false,
    },
    {
      field: "demandNote",
      headerName: "Demand Note Amount",
      width: 200,
      sortable: false,
    },
    {
      field: "totalAmountPaid",
      headerName: "Total Amount Paid",
      width: 200,
      sortable: false,
    },
    {
      field: "ecoRestorationCostPaid",
      headerName: "Eco-Restoration Cost Paid",
      width: 220,
      sortable: false,
    },
    {
      field: "adminChargesPaid",
      headerName: "Admin Charges Paid",
      width: 200,
      sortable: false,
    },
    {
      field: "created_at",
      headerName: "Project Selection Date",
      width: 200,
      sortable: false,
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      width: 200,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      sortable: false,
    },
  ];

  const exportToExcelEntity = () => {
    setQueryParams({
      ...queryParams,
      download: true,
    });
  };

  const handleSort = (sortByField: any) => {
    setQueryParams({
      ...queryParams,
      sortBy: `${sortByField[0]?.field}:${sortByField[0]?.sort.toUpperCase()}`,
    });
  };

  const handlePagination = (page: number) => {
    setQueryParams({
      ...queryParams,
      offset: queryParams.limit * page,
    });
  };

  return (
    <DashboardWrapper
      title={"Monitoring"}
      buttonIcon="/assets/svg/roundPlusIcon.svg"
      buttonText=""
      onButtonClick={() => {}}
      showBtn={false}
      showBackBtn={true}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: "2%",
            padding: "10px 1%",
            background: "white",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          }}
        >
          <SearchBar
            getText={(e: string) => {
              setQueryParams((prev) => ({
                ...prev,
                searchText: `${e}`,
                offset: 0,
              }));
            }}
            customStyles={{
              wrapper: {
                flex: 1,
                minWidth: "240px",
              },
            }}
          />
          <EntityFilter
            source="landOwnership"
            filters={{
              status: queryParams.status,
              searchText: queryParams.searchText,
            }}
            value={queryParams.entityIds?.split(",") ?? []}
            onChange={(ids) => {
              setQueryParams((prev) => ({
                ...prev,
                entityIds: ids.length ? ids.join(",") : undefined,
                offset: 0,
              }));
            }}
          />
          <Button onClick={exportToExcelEntity} text={"Export"} />
        </Box>
        {isLoading || isFetching ? (
          <Box {...styles("flexContainer")}>
            <CircularProgress />
          </Box>
        ) : tableRows.length > 0 ? (
          <DataGrid
            rows={tableRows}
            columns={tableColumns}
            rowCount={rowCount / queryParams.limit}
            sortingMode="server"
            onSortModelChange={handleSort}
            pageSize={1}
            onChangePage={handlePagination}
            pageNumber={queryParams.offset / queryParams.limit + 1}
            loading={isLoading}
            columnBuffer={20}
          />
        ) : (
          <EmptyView
            heading="No Entry Found"
            subheading=""
            onClick={() => {}}
            buttonText={""}
          />
        )}
      </Box>
    </DashboardWrapper>
  );
};

export default Monitoring;
